import React from 'react';

/**
 * FilesListView Component
 * Table-based alternate file explorer layout
 */
export default function FilesListView({
    files = [],
    selectedFile,
    onSelectFile,
    onPreviewFile,
    onDownloadFile,
    onPublishToCRE,
}) {
    if (!files.length) {
        return (
            <div className="py-16 text-center bg-white dark:bg-[#141c2b] rounded-xl border border-dashed border-[#c1c6d7] dark:border-[#344258]">
                <span className="material-symbols-outlined text-5xl text-[#727786] dark:text-[#5c687e] mb-3 block">
                    folder_open
                </span>
                <p className="text-base font-semibold text-[#0b1c30] dark:text-[#eaf1ff]">
                    No files in this folder
                </p>
            </div>
        );
    }

    return (
        <div className="border border-[#c1c6d7] dark:border-[#344258] rounded-xl overflow-hidden shadow-sm bg-white dark:bg-[#1a2538]">
            <table className="w-full text-left border-collapse">
                <thead className="bg-[#eff4ff] dark:bg-[#212f45] border-b border-[#c1c6d7] dark:border-[#344258]">
                    <tr>
                        <th className="px-6 py-3 text-xs font-semibold text-[#555d73] dark:text-[#9aa5bc] uppercase tracking-wider">
                            Name
                        </th>
                        <th className="px-6 py-3 text-xs font-semibold text-[#555d73] dark:text-[#9aa5bc] uppercase tracking-wider">
                            Version
                        </th>
                        <th className="px-6 py-3 text-xs font-semibold text-[#555d73] dark:text-[#9aa5bc] uppercase tracking-wider">
                            Type
                        </th>
                        <th className="px-6 py-3 text-xs font-semibold text-[#555d73] dark:text-[#9aa5bc] uppercase tracking-wider">
                            Size
                        </th>
                        <th className="px-6 py-3 text-xs font-semibold text-[#555d73] dark:text-[#9aa5bc] uppercase tracking-wider">
                            Uploaded By
                        </th>
                        <th className="px-6 py-3 text-xs font-semibold text-[#555d73] dark:text-[#9aa5bc] uppercase tracking-wider">
                            Date
                        </th>
                        <th className="px-6 py-3 text-xs font-semibold text-[#555d73] dark:text-[#9aa5bc] uppercase tracking-wider text-right">
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[#c1c6d7]/60 dark:divide-[#344258]">
                    {files.map((file) => {
                        const isSelected = selectedFile?.id === file.id;
                        const isDeliverable = file.folderCode === '05' || file.folderId?.includes('_05');
                        const isCRESynced = file.publishedToCRE || !!file.creFileId;

                        return (
                            <tr
                                key={file.id}
                                onClick={() => onSelectFile?.(file)}
                                className={`
                                    cursor-pointer transition-colors
                                    ${isSelected ? 'bg-[#eff4ff] dark:bg-[#212f45]/70' : 'hover:bg-[#f8f9ff] dark:hover:bg-[#212f45]/40'}
                                `}
                            >
                                <td className="px-6 py-3.5">
                                    <div className="flex items-center gap-3">
                                        <span className="material-symbols-outlined text-[#0070F3] text-xl">
                                            {file.icon || 'description'}
                                        </span>
                                        <div>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="font-semibold text-sm text-[#0b1c30] dark:text-[#eaf1ff]">
                                                    {file.name}
                                                </p>
                                                {isCRESynced && (
                                                    <span
                                                        title={file.creFileId ? `Published to CRE: ${file.creFileId}` : 'Synced with CRE'}
                                                        className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[10px] font-bold bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30"
                                                    >
                                                        <span className="material-symbols-outlined text-[11px]">cloud_done</span>
                                                        CRE
                                                    </span>
                                                )}
                                                {file.revisionRound && (
                                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[10px] font-bold bg-purple-500/15 text-purple-700 dark:text-purple-300 border border-purple-500/30">
                                                        {file.revisionRound}
                                                    </span>
                                                )}
                                                {file.status === 'archived' && (
                                                    <span className="px-1.5 py-0.2 rounded text-[10px] font-bold bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30">
                                                        Archived
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-[#727786] dark:text-[#9aa5bc]">
                                                {file.subfolderCategory ? `${file.subfolderCategory} • ` : ''}{file.category || file.project}
                                            </p>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-3.5">
                                    <span className="px-2 py-0.5 bg-[#e6f8f5] dark:bg-[#0b3d37] text-[#006f66] dark:text-[#89f5e7] rounded-full text-xs font-bold">
                                        {file.version}
                                    </span>
                                </td>
                                <td className="px-6 py-3.5 text-sm text-[#414754] dark:text-[#9aa5bc]">
                                    {file.type}
                                </td>
                                <td className="px-6 py-3.5 text-sm text-[#414754] dark:text-[#9aa5bc]">
                                    {file.size}
                                </td>
                                <td className="px-6 py-3.5 text-sm text-[#0b1c30] dark:text-[#eaf1ff]">
                                    {file.uploadedBy}
                                </td>
                                <td className="px-6 py-3.5 text-sm text-[#414754] dark:text-[#9aa5bc]">
                                    {file.date}
                                </td>
                                <td className="px-6 py-3.5 text-right">
                                    <div className="flex justify-end items-center gap-2">
                                        {isDeliverable && !isCRESynced && (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onPublishToCRE?.(file);
                                                }}
                                                title="Publish Deliverable to CRE"
                                                className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-semibold flex items-center gap-1 transition-all"
                                            >
                                                <span className="material-symbols-outlined text-sm leading-none block">publish</span>
                                                Publish to CRE
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onPreviewFile?.(file);
                                            }}
                                            title="Preview"
                                            className="p-1 text-[#727786] dark:text-[#9aa5bc] hover:text-[#0070F3] transition-colors"
                                        >
                                            <span className="material-symbols-outlined text-lg leading-none block">
                                                visibility
                                            </span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDownloadFile?.(file);
                                            }}
                                            title="Download"
                                            className="p-1 text-[#727786] dark:text-[#9aa5bc] hover:text-[#0070F3] transition-colors"
                                        >
                                            <span className="material-symbols-outlined text-lg leading-none block">
                                                download
                                            </span>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
